import { InputLabel } from "@/components/Elements/Texts/InputLabel";
import { SubmitHandler, useForm } from "react-hook-form";
// state
import { RadioGroupForm } from "@/components/Elements/Forms/RadioGroupForm";
import { SelectForm } from "@/components/Elements/Forms/SelectForm";
import { TextFieldForm } from "@/components/Elements/Forms/TextFieldForm";
import {
  HStack,
  HStackStyler as HItem,
  StackItem,
} from "@/components/Elements/Layouts/Stack";
import { NextNavigaterButton } from "@/features/LG/components/shared";
import {
  BuildingStructure,
  buildingStructureState,
  buildingYearState,
  LayoutType,
  layoutTypeState,
  livingAreaState,
  locatedFloorState,
  totalFloorState,
} from "@/store/propertyState";
import { useRecoilState } from "recoil";
import {
  floorValidation,
  livingAreaValidation,
  requiredValidation,
} from "../../../types/validations";
import { buildingOptions } from "../type";

type FormInput = {
  buildingYear: string;
  buildingStructure: BuildingStructure;
  livingArea: string;
  landArea: string;
  layoutType: LayoutType;
  locatedFloor: string;
  totalFloor: string;
};

export const Apartment: React.FC<{ next: string }> = ({ next }) => {
  // global state
  const [buildingYear, setBuildingYear] = useRecoilState(buildingYearState);
  const [buildingStructure, setBuildingStructure] = useRecoilState(
    buildingStructureState
  );
  const [livingArea, setLivingArea] = useRecoilState(livingAreaState);
  const [layoutType, setLayoutType] = useRecoilState(layoutTypeState);
  const [locatedFloor, setLocatedFloor] = useRecoilState(locatedFloorState);
  const [totalFloor, setTotalFloor] = useRecoilState(totalFloorState);

  // react-hook-form
  const form = useForm<FormInput>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      buildingYear: buildingYear,
      buildingStructure: buildingStructure,
      livingArea: livingArea,
      layoutType: layoutType,
      locatedFloor: locatedFloor,
      totalFloor: totalFloor,
    },
  });
  const {
    getValues,
    handleSubmit,
    formState: { isValid },
  } = form;

  const onSubmit: SubmitHandler<FormInput> = (data) => {
    setBuildingYear(data.buildingYear);
    setBuildingStructure(data.buildingStructure);
    setLivingArea(data.livingArea);
    setLayoutType(data.layoutType);
    setLocatedFloor(data.locatedFloor);
    setTotalFloor(data.totalFloor);
  };

  return (
    <>
      <div>
        <InputLabel required label="建築年" />
        <SelectForm
          form={form}
          name="buildingYear"
          placeholder="未選択"
          options={buildingOptions}
          rules={{
            ...requiredValidation,
          }}
        />
      </div>

      <div>
        <InputLabel label="建築構造" />
        <RadioGroupForm
          form={form}
          name="buildingStructure"
          options={[
            {
              label: "鉄筋系（鉄筋コンクリート）",
              value: "reinforced_concrete",
            },
            {
              label: "鉄骨系（軽量鉄骨など）",
              value: "light_weight_steel",
            },
            { label: "木造", value: "wood" },
            { label: "その他", value: "" },
          ]}
        />
      </div>

      <HStack spacing={3}>
        <HItem>
          <InputLabel required label="専有面積" />
          <TextFieldForm
            type="number"
            form={form}
            name="livingArea"
            placeholder="50"
            rules={{
              ...requiredValidation,
            }}
          />
        </HItem>
        <HItem>
          <InputLabel label="間取り" />
          <SelectForm
            form={form}
            name="layoutType"
            placeholder="未選択"
            options={[
              { label: "未選択", value: "" },
              { label: "ワンルーム", value: "ワンルーム" },
              { label: "1K", value: "1K" },
              { label: "1DK/LDK", value: "1DK/LDK" },
              { label: "2K/DK/LDK", value: "2K/DK/LDK" },
              { label: "3K/DK/LDK", value: "3K/DK/LDK" },
              { label: "4K/DK/LDK", value: "4K/DK/LDK" },
              { label: "5K以上", value: "5K以上" },
            ]}
          />
        </HItem>
      </HStack>

      <HStack spacing={3}>
        <HItem>
          <InputLabel label="所在階" />
          <TextFieldForm
            type="number"
            form={form}
            name="locatedFloor"
            placeholder="5"
            rules={{
              ...floorValidation,
              validate: {
                limit: (value: string) => {
                  const tmp = getValues("totalFloor");
                  if (value && tmp) {
                    if (Number(value) > Number(tmp)) {
                      return "所在階は建物の階数以下を指定してください";
                    }
                  }
                  return true;
                },
              },
            }}
          />
        </HItem>
        <HItem>
          <InputLabel label="建物の階数" />
          <TextFieldForm
            type="number"
            form={form}
            name="totalFloor"
            placeholder="5"
            rules={{ ...floorValidation }}
          />
        </HItem>
      </HStack>

      <StackItem>
        <NextNavigaterButton
          label="次へ"
          isValid={isValid}
          next={next}
          onClick={handleSubmit(onSubmit)}
        />
      </StackItem>
    </>
  );
};
